import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, RefreshCw, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEALTH = {
  live: { ring: "border-emerald-500/40 bg-emerald-500/5", text: "text-emerald-300", Icon: CheckCircle2 },
  pending: { ring: "border-amber-500/40 bg-amber-500/5", text: "text-amber-300", Icon: Clock },
  blocked: { ring: "border-rose-500/40 bg-rose-500/5", text: "text-rose-300", Icon: AlertTriangle },
  off: { ring: "border-border/40 bg-muted/10", text: "text-muted-foreground", Icon: XCircle },
};

const StatusIcon = ({ status }) => {
  if (status === "delivered") return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />;
  if (status === "failed" || status === "undelivered") return <XCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />;
  return <Clock className="w-3.5 h-3.5 text-amber-400 shrink-0" />;
};

export const SmsDelivery = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/sms/delivery`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const h = HEALTH[data.health] || HEALTH.off;
  const c = data.counts || {};
  return (
    <div className={`rounded-xl border p-4 space-y-3 ${h.ring}`} data-testid="sms-delivery-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <h.Icon className={`w-5 h-5 mt-0.5 ${h.text}`} />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" />Patient text messages</p>
            <p className={`font-semibold mt-0.5 ${h.text}`} data-testid="sms-health-headline">{data.headline}</p>
            {data.from_number && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Automated texts go out from <span className="font-mono">{data.from_number}</span>; every message tells patients to call/text <span className="text-primary font-medium">{data.business_phone}</span>. Patient replies are forwarded to your phone.
              </p>
            )}
          </div>
        </div>
        <button type="button" onClick={load} disabled={loading} data-testid="sms-refresh-btn"
          className="text-[11px] px-2.5 py-1 rounded-full border border-primary/40 text-primary hover:bg-primary/10 flex items-center gap-1 disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />Check again
        </button>
      </div>
      {data.recent?.length > 0 && (
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-black/30 border border-border/40 p-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Delivered</p><p className="text-xl font-bold text-emerald-300" data-testid="sms-count-delivered">{c.delivered ?? 0}</p></div>
          <div className="rounded-lg bg-black/30 border border-border/40 p-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Blocked</p><p className="text-xl font-bold text-rose-300" data-testid="sms-count-failed">{c.failed ?? 0}</p></div>
          <div className="rounded-lg bg-black/30 border border-border/40 p-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Balance</p><p className="text-xl font-bold text-primary" data-testid="sms-balance">${Number(data.balance || 0).toFixed(2)}</p></div>
        </div>
      )}
      {data.last_failure && data.health !== "live" && (
        <p className="text-xs text-rose-200/90" data-testid="sms-last-failure">Most recent block: <span className="font-medium">{data.last_failure.reason}</span></p>
      )}
      {data.recent?.length > 0 && (
        <div>
          <button type="button" onClick={() => setOpen(o => !o)} data-testid="sms-toggle-recent" className="text-[11px] text-muted-foreground hover:text-primary underline-offset-2 hover:underline">
            {open ? "Hide" : "Show"} last {data.recent.length} texts
          </button>
          {open && (
            <ul className="mt-2 divide-y divide-border/20 text-xs" data-testid="sms-recent-list">
              {data.recent.map((m, i) => (
                <li key={i} className="py-1.5 flex items-start gap-2">
                  <StatusIcon status={m.status} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate"><span className="font-mono text-muted-foreground">{m.to}</span> · <span className={m.status === "delivered" ? "text-emerald-300" : (m.status === "failed" || m.status === "undelivered") ? "text-rose-300" : "text-amber-300"}>{m.reason}</span></p>
                    <p className="text-muted-foreground/70 truncate">{m.preview}</p>
                  </div>
                  <span className="text-muted-foreground/60 shrink-0">{m.date ? new Date(m.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
